import { useState } from 'react';
import { Paper, TextField, Button, Typography, Alert, Box, Link as MuiLink, Stack, Divider } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PRIMARY, NAV_BG } from '../theme';
import BrandLogo from '../components/BrandLogo';

export default function LoginPage() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const u = await login(email, password);
      nav(u.mustChangePassword ? '/change-password' : '/');
    } catch (e) {
      setErr(e?.response?.data?.message || 'Invalid email or password');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f4f6fb' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: { xs: '0 0 0', md: '1 1 55%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${NAV_BG} 0%, ${PRIMARY} 60%, #1B3A78 100%)`
        }}
      >
        <Box sx={{
          position: 'absolute', top: -120, right: -120, width: 380, height: 380, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.18) 0%, rgba(245,166,35,0) 70%)'
        }}/>
        <Box sx={{
          position: 'absolute', bottom: -160, left: -100, width: 460, height: 460, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(27,58,120,0.55) 0%, rgba(27,58,120,0) 70%)'
        }}/>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <BrandLogo brand="leano" variant="light" height={56} />
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.15, mb: 2 }}>
            Reseller Portal
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.85, mb: 4 }}>
            Manage your tenants, users, and support tickets &mdash; all in one place.
          </Typography>
          <Stack direction="row" spacing={3} sx={{ opacity: 0.9 }}>
            <Feature label="Tenant lifecycle" />
            <Feature label="Role-based access" />
            <Feature label="Support tickets" />
          </Stack>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: 1, textTransform: 'uppercase' }}>
            Powered by
          </Typography>
          <BrandLogo brand="izzat" variant="light" height={28} />
        </Box>
      </Box>

      {/* Form panel */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 3, md: 6 } }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Stack alignItems="center" spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
            <BrandLogo brand="leano" height={48} />
            <Typography variant="body2" color="text.secondary">Reseller Portal</Typography>
          </Stack>

          <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={0.5} mb={3}>
              <Typography variant="h5" sx={{ color: PRIMARY, fontWeight: 700 }}>Welcome back</Typography>
              <Typography variant="body2" color="text.secondary">Sign in to your reseller account</Typography>
            </Stack>
            <form onSubmit={submit}>
              <Stack spacing={2.25}>
                {err && <Alert severity="error">{err}</Alert>}
                <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoFocus autoComplete="email" />
                <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth autoComplete="current-password" />
                <Button type="submit" variant="contained" size="large" disabled={loading} sx={{ py: 1.25, fontWeight: 600 }}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
                <MuiLink component={Link} to="/forgot-password" sx={{ textAlign: 'center', fontSize: 14 }}>
                  Forgot password?
                </MuiLink>
              </Stack>
            </form>
          </Paper>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1, textTransform: 'uppercase' }}>
                Powered by
              </Typography>
              <BrandLogo brand="izzat" height={22} />
            </Box>
            <Divider sx={{ my: 2, display: { xs: 'block', md: 'none' } }} />
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Leano · All rights reserved
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Feature({ label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#F5A623' }} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}
